import { useMemo } from 'react';
import {
  batchDisplayStatus,
  canAmendOrder,
  type DemOrderBatch,
} from '../../lib/demOrderBatches';
import type { OrderItemBlockInfo } from '../../lib/demOrderBlockWarnings';

export type OrderBatchItemView = {
  item_code: string;
  description: string;
  qty: number;
  uom: string;
  batch_id?: string | null;
};

function isDeliveryFeeLine(item: OrderBatchItemView): boolean {
  const code = item.item_code;
  const desc = item.description;
  return (
    code === 'NEW PRODUCT' ||
    code === '401090' ||
    code === '601158' ||
    desc.includes('401090') ||
    desc.includes('601158') ||
    desc.includes('Delivery Charge') ||
    desc.includes('Delivery Fee')
  );
}

type Props = {
  orderStatus: string;
  batches?: DemOrderBatch[] | null;
  items: OrderBatchItemView[];
  onAmend?: () => void;
  amendDisabled?: boolean;
  /** Web/WA: highlight delivery fee lines and show waived state. */
  waiveDelivery?: boolean;
  styleDeliveryFee?: boolean;
  /** item_code (upper) → block warning info */
  itemWarnings?: Map<string, OrderItemBlockInfo>;
};

export function OrderBatchesItemsPanel({
  orderStatus,
  batches,
  items,
  onAmend,
  amendDisabled,
  waiveDelivery = false,
  styleDeliveryFee = false,
  itemWarnings,
}: Props) {
  const sortedBatches = useMemo(() => {
    const list = [...(batches ?? [])].sort((a, b) => a.batch_no - b.batch_no);
    if (list.length > 0) return list;
    return [
      {
        id: '',
        order_id: '',
        batch_no: 0,
        reason: null,
        created_by: '',
        created_at: '',
        exported_by: null,
        exported_at: null,
      } satisfies DemOrderBatch,
    ];
  }, [batches]);

  const batchIdSet = useMemo(
    () => new Set(sortedBatches.map((b) => b.id).filter(Boolean)),
    [sortedBatches],
  );

  const fallbackBatchId = sortedBatches[0]?.id ?? '';

  const itemsByBatchId = useMemo(() => {
    const map = new Map<string, OrderBatchItemView[]>();
    for (const batch of sortedBatches) {
      map.set(batch.id || '__legacy__', []);
    }
    for (const item of items) {
      const raw = item.batch_id?.trim() ?? '';
      const key =
        raw && batchIdSet.has(raw) ? raw : fallbackBatchId || '__legacy__';
      const bucket = map.get(key) ?? [];
      bucket.push(item);
      map.set(key, bucket);
    }
    return map;
  }, [items, sortedBatches, batchIdSet, fallbackBatchId]);

  const showAmend = typeof onAmend === 'function' && canAmendOrder(orderStatus);

  return (
    <div className="dem-order-batches">
      <div className="dem-order-batches__head">
        <h3 className="dem-web-order-detail__items-title">Items</h3>
        {showAmend ? (
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={amendDisabled}
            onClick={onAmend}
          >
            Add items
          </button>
        ) : null}
      </div>

      {sortedBatches.map((batch) => {
        const key = batch.id || '__legacy__';
        const batchItems = itemsByBatchId.get(key) ?? [];
        const status = batchDisplayStatus(batch, orderStatus);
        const title = batch.batch_no === 0 ? 'Original order' : `Add-on #${batch.batch_no}`;
        return (
          <section key={key || `batch-${batch.batch_no}`} className="dem-order-batches__block">
            <div className="dem-order-batches__meta">
              <div className="dem-order-batches__title-row">
                <strong>{title}</strong>
                <span
                  className={`dem-order-batches__status dem-order-batches__status--${status.toLowerCase()}`}
                >
                  {status}
                </span>
              </div>
              <div className="dem-order-batches__by">
                <span>Creator: {batch.created_by.trim() || '—'}</span>
                <span>Exporter: {batch.exported_by?.trim() || '—'}</span>
              </div>
              {batch.batch_no > 0 && batch.reason ? (
                <p className="dem-order-batches__reason">Reason: {batch.reason}</p>
              ) : null}
            </div>
            <div className="dem-web-order-detail__table">
              <div className="dem-web-order-detail__table-head" aria-hidden>
                <span>Item code</span>
                <span>Description</span>
                <span>Qty</span>
                <span>UOM</span>
              </div>
              {batchItems.length === 0 ? (
                <div className="dem-web-order-detail__table-row dem-order-batches__empty">
                  <span>—</span>
                  <span>No lines</span>
                  <span>—</span>
                  <span>—</span>
                </div>
              ) : (
                batchItems.map((item, index) => {
                  const delivery = styleDeliveryFee && isDeliveryFeeLine(item);
                  const waived = waiveDelivery && delivery;
                  const label = delivery ? 'DELIVERY FEE' : item.item_code || '—';
                  const warning =
                    itemWarnings?.get(item.item_code.trim().toUpperCase())?.warning ?? '';
                  return (
                    <div
                      key={`${key}-${item.item_code}-${index}`}
                      className="dem-web-order-detail__line-wrap"
                    >
                      <div
                        className={`dem-web-order-detail__table-row${delivery && !waived ? ' dem-web-order-detail__table-row--fee' : ''}${waived ? ' dem-web-order-detail__table-row--waived' : ''}`}
                      >
                        <span>{label}</span>
                        <span>
                          {waived ? `${item.description} (WAIVED)` : item.description || '—'}
                        </span>
                        <span>{item.qty}</span>
                        <span>{item.uom || '—'}</span>
                      </div>
                      {warning ? (
                        <p className="dem-web-order-detail__item-block" title={warning}>
                          {warning}
                        </p>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
